import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, MapPin, MessageCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Logo } from "@/components/Logo";
import { Reveal } from "@/components/Reveal";
import { WHATSAPP_NUMBER, waLink } from "@/lib/whatsapp";

export const Footer = () => {
  const { t } = useLang();
  const year = new Date().getFullYear();

  const menu = [
    { to: "/", key: "home" },
    { to: "/about", key: "about" },
    { to: "/services", key: "services" },
    { to: "/portfolio", key: "portfolio" },
    { to: "/contact", key: "contact" },
  ];

  return (
    <footer className="bg-espresso text-offwhite" data-testid="footer">
      <div className="wrap pb-10 pt-16 sm:pt-20">
        <Reveal>
          <h2 className="font-display text-[clamp(2.6rem,9vw,8.5rem)] font-light leading-[0.95] tracking-tight text-offwhite/95">
            SAMASTA STUDIO
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-12 border-t border-offwhite/15 pt-12 sm:grid-cols-2 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="flex items-center gap-3">
              <Logo className="h-7 w-7 text-offwhite" />
              <span className="font-grotesk text-xs font-medium uppercase tracking-[0.25em] text-offwhite/70">
                Bali, Indonesia
              </span>
            </p>
            <p className="mt-6 max-w-xs text-sm font-light leading-relaxed text-offwhite/60">
              {t.footer.blurb}
            </p>
          </div>

          <div className="lg:col-span-2">
            <p className="eyebrow text-offwhite/50">{t.footer.menuTitle}</p>
            <ul className="mt-5 space-y-3">
              {menu.map((m) => (
                <li key={m.key}>
                  <Link
                    to={m.to}
                    data-testid={`footer-menu-${m.key}`}
                    className="text-sm font-light text-offwhite/75 transition-colors duration-300 hover:text-terracotta"
                  >
                    {t.nav[m.key]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="eyebrow text-offwhite/50">{t.footer.servicesTitle}</p>
            <ul className="mt-5 space-y-3">
              {t.services.slice(0, 6).map((s) => (
                <li key={s.id}>
                  <Link
                    to="/services"
                    data-testid={`footer-service-${s.id}`}
                    className="text-sm font-light text-offwhite/75 transition-colors duration-300 hover:text-terracotta"
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="eyebrow text-offwhite/50">{t.footer.contactTitle}</p>
            <ul className="mt-5 space-y-4 text-sm font-light text-offwhite/75">
              <li>
                <a
                  href={waLink(t.nav.waMessage)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-whatsapp-link"
                  className="flex items-center gap-3 transition-colors duration-300 hover:text-terracotta"
                >
                  <MessageCircle className="h-4 w-4 text-terracotta" /> +{WHATSAPP_NUMBER}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-terracotta" /> {t.contact.studio.email}
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 text-terracotta" /> {t.contact.studio.location}
              </li>
            </ul>
            <a
              href={waLink(t.nav.waMessage)}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-konsultasi-link"
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-offwhite/25 px-5 py-2.5 text-sm transition-colors duration-300 hover:border-terracotta hover:text-terracotta"
            >
              {t.nav.cta}
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div className="mt-16 flex flex-col justify-between gap-3 border-t border-offwhite/15 pt-6 text-xs font-light text-offwhite/45 sm:flex-row">
          <p data-testid="footer-copyright">
            © {year} Samasta Studio — {t.common.rights}
          </p>
          <p className="font-grotesk uppercase tracking-[0.25em]">{t.common.craftedInBali}</p>
        </div>
      </div>
    </footer>
  );
};
